import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Bot, ArrowRight, TrendingUp, Palette, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, register, user } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("operator@autostore.ai");
  const [password, setPassword] = useState("autostore123");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name || "Operator");
      toast.success(mode === "login" ? "Welcome back, operator." : "Account created.");
    } catch (e2) {
      setErr(apiError(e2, "Authentication failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#090B0E] text-slate-100">
      {user && <Navigate to="/" replace />}
      <div className="hidden lg:flex flex-col justify-between w-[45%] p-12 bg-gradient-to-br from-[#0C1119] to-[#0A0D12] border-r border-slate-800/80 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-[0_0_24px_rgba(16,185,129,0.5)]">
            <Bot className="w-6 h-6 text-slate-950" />
          </div>
          <div>
            <div className="font-head font-bold text-lg">AutoStore AI</div>
            <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">Autonomous POD Console</div>
          </div>
        </div>
        <div className="relative space-y-6">
          <h1 className="font-head text-4xl font-bold leading-tight">Run print-on-demand stores<br />on <span className="text-emerald-400">autopilot.</span></h1>
          <p className="text-slate-400 max-w-md">Agents research niches, generate designs, write SEO listings and optimize campaigns — you approve only the money and publish moments.</p>
          <div className="space-y-3 pt-4">
            {[[TrendingUp, "Niche & trend research agent"], [Palette, "AI design & listing generation"], [ShieldCheck, "Human approval gates on spend & publish"]].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center"><Icon className="w-4 h-4 text-emerald-400" /></div>
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-600 font-mono relative">POD · Shopify · Printify · Etsy assisted</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="auth-form">
          <div>
            <h2 className="font-head text-2xl font-bold">{mode === "login" ? "Operator sign in" : "Create operator account"}</h2>
            <p className="text-sm text-slate-400 mt-1">Demo credentials are pre-filled.</p>
          </div>
          {mode === "register" && (
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Name</label>
              <input data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#12161F] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm" placeholder="Your name" />
            </div>
          )}
          <div>
            <label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Email</label>
            <input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#12161F] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm" required />
          </div>
          <div>
            <label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Password</label>
            <input data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#12161F] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm" required />
          </div>
          {err && <div data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
          <button data-testid="submit-auth-button" disabled={loading} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-60">
            {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </button>
          <div className="text-center text-sm text-slate-400">
            {mode === "login" ? "No account yet?" : "Already have an account?"}{" "}
            <button type="button" data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} className="text-emerald-400 hover:underline">
              {mode === "login" ? "Register" : "Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
